import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  navItemIconRecipe,
  navItemRecipe,
  type NavItemRecipeProps,
} from '../recipes/nav.recipe';
import { Button, type ButtonProps } from './Button';

export interface NavItemProps
  extends Omit<ButtonProps, 'variant' | 'children'>,
    NavItemRecipeProps {
  /** Usually a lucide icon, but an `<img>` works — the settings sidebar uses
   *  provider logos for the payment rows. */
  icon?: React.ReactNode;
  label: React.ReactNode;
  /** Marks the current page: full black, where the other rows are half. */
  active?: boolean;
  /** Right-aligned extra: a chevron for a group, controls in edit mode. */
  trailing?: React.ReactNode;
}

/**
 * One row of a sidebar.
 *
 *   <NavItem icon={<Home />} label="Home" active={isActive} onClick={go} />
 *
 * To navigate, wrap it the way the router wants:
 *
 *   <Link to={path}><NavItem icon={<Home />} label="Home" /></Link>
 *
 * The icon is cloned so the animation classes land on it without every call
 * site repeating them. That means `icon` must be a single element — an icon
 * component or an `<img>`, not a fragment.
 */
export const NavItem = React.forwardRef<HTMLButtonElement, NavItemProps>(
  function NavItem(
    {
      icon,
      label,
      active = false,
      nested,
      trailing,
      muted,
      className,
      ...props
    },
    ref
  ) {
    return (
      <Button
        ref={ref}
        // Always ghost: the row's own recipe paints the active and hover
        // surfaces, so Button's `secondary` is not borrowed for a job it was
        // never sized for.
        variant="ghost"
        className={cn(navItemRecipe({ active, nested, muted }), className)}
        // The current page is told apart by brightness alone, which a screen
        // reader cannot see.
        aria-current={active ? 'page' : undefined}
        {...props}
      >
        {React.isValidElement(icon)
          ? React.cloneElement(
              icon as React.ReactElement<{ className?: string }>,
              {
                className: cn(
                  navItemIconRecipe(),
                  (icon as React.ReactElement<{ className?: string }>).props
                    .className
                ),
              }
            )
          : icon}
        {label}
        {trailing ? (
          <span className="ml-auto flex items-center">{trailing}</span>
        ) : null}
      </Button>
    );
  }
);
